import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Grid, Card, CardContent, Divider, Alert
} from '@mui/material';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../components/CartProvider';
import { Autocomplete, Libraries, useJsApiLoader } from '@react-google-maps/api';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';

const API_BASE = process.env.REACT_APP_API_URL;
const PLACES_API_PUBLIC = process.env.REACT_APP_PLACES_API_KEY;
const libraries: Libraries = ['places'];

const Checkout: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { cartItems, total, clearCart } = useCart();

  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
  });

  const [shippingOptions, setShippingOptions] = useState<any[]>([]);
  const [selectedRate, setSelectedRate] = useState<string>('');
  const [fetchingRates, setFetchingRates] = useState(false);
  const [shippingConfig, setShippingConfig] = useState<any>(null);
  const [shipmentId, setShipmentId] = useState<string>('');
  const [shippingCost, setShippingCost] = useState<number>(0);

  const subtotal = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

  const fetchShippingConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/config`, {
        headers: {
          'Content-Type': 'application/json',
          'x-client-id': "1",
        },
      });
      if (!res.ok) throw new Error('Failed to fetch shipping config');
      const config = await res.json();
      setShippingConfig(config);
    } catch (err) {
      console.error('Error fetching config', err);
    }
  };

  const fetchShippingRates = async (info: any) => {
    const { addressLine1, city, state, zip } = info;
    if (!addressLine1 || !city || !state || !zip) return;

    if (shippingConfig){
      if(subtotal >= Number(shippingConfig.free_shipping_threshold)){
        setSelectedRate('free');
        return;
      }else if (shippingConfig.shipping_method === 'flat_rate'){
        setSelectedRate('flat');
      }
    }

    setFetchingRates(true);
    try {
      const res = await fetch(`${API_BASE}/api/shipping/rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          toAddress: { ...info, country: 'US' },
          parcel: { length: '10', width: '8', height: '4', distance_unit: 'in', weight: '2', mass_unit: 'lb' }
        })
      });
      if (!res.ok) throw new Error('Failed to fetch shipping rates');
      const data = await res.json();
      setShippingOptions(data.rates);
      setShipmentId(data.shipmentId);
      if (data.length > 0) setSelectedRate(data[0].id);
    } catch (err) {
      console.error('Rate fetch error:', err);
    } finally {
      setFetchingRates(false);
    }
  };

  useEffect(() => { fetchShippingConfig(); }, []);

  useEffect(() => {
    if (!shippingConfig) return;

    if (subtotal >= Number(shippingConfig.free_shipping_threshold)) {
      setShippingCost(0);
    } else if (shippingConfig.shipping_method === 'flat_rate') {
      if (shippingConfig.flat_rate_type === 'amount') {
        setShippingCost(Number(shippingConfig.flat_rate_value));
      } else if (shippingConfig.flat_rate_type === 'percentage') {
        setShippingCost((subtotal * Number(shippingConfig.flat_rate_value)) / 100);
      }
    } else if (shippingConfig.shipping_method === 'carrier') {
      const selected = shippingOptions.find(opt => opt.id === selectedRate);
      setShippingCost(selected ? Number(selected.rate) : 0);
    }
  }, [shippingConfig, subtotal, selectedRate, shippingOptions]);

  const navigate = useNavigate();

  useEffect(() => {
    if (cartItems.length === 0 && !success) navigate('/');
  }, [cartItems, success, navigate]);

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: PLACES_API_PUBLIC!,
    libraries,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomerInfo({ ...customerInfo, [name]: value });
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.address_components) return;

    const components: any = {};
    place.address_components.forEach((comp) => {
      if (comp.types.includes('street_number')) components.street_number = comp.long_name;
      if (comp.types.includes('route')) components.route = comp.long_name;
      if (comp.types.includes('locality')) components.city = comp.long_name;
      if (comp.types.includes('administrative_area_level_1')) components.state = comp.short_name;
      if (comp.types.includes('postal_code')) components.zip = comp.long_name;
    });

    const updatedInfo = {
      ...customerInfo,
      addressLine1: [components.street_number, components.route].filter(Boolean).join(' '),
      city: components.city || '',
      state: components.state || '',
      zip: components.zip || '',
    };

    setCustomerInfo(updatedInfo);
    fetchShippingRates(updatedInfo);
  };

  const validateAddress = () => {
    const { addressLine1, city, state, zip, email } = customerInfo;
    if (!addressLine1 || !city || !state || !zip || !email) {
      setError('Please enter valid information including email, city, state, and zip code.');
      return false;
    }
    return true;
  };

  const submitOrder = async (stripePaymentId: string) => {
    setSubmittingOrder(true);
    try {
      const res = await fetch(`${API_BASE}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerInfo,
          items: cartItems,
          total: subtotal + shippingCost,
          stripePaymentId,
          shippingRateId: selectedRate,
        }),
      });

      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || 'Failed to save order');
      }
    } finally {
      setSubmittingOrder(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !validateAddress()) return;

    setLoading(true);
    setError('');
    console.log(selectedRate);

    try {
      const response = await fetch(`${API_BASE}/api/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cartItems.map((item) => ({
            name: item.name,
            quantity: item.quantity,
            price: item.price * 100,
            variationId: item.variationId
          })),
          customer: customerInfo,
          selectedRateId: selectedRate,
          shipmentId: shipmentId
        }),
      });

      if (!response.ok) throw new Error('Server error');

      const { clientSecret, paymentIntentId } = await response.json();
      const cardElement = elements.getElement(CardElement);
      const paymentResult = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement!,
          billing_details: {
            name: customerInfo.name,
            email: customerInfo.email,
            address: {
              line1: customerInfo.addressLine1,
              line2: customerInfo.addressLine2,
              postal_code: customerInfo.zip,
              state: customerInfo.state,
              city: customerInfo.city,
            },
          },
        },
      });

      if (paymentResult.error) throw new Error(paymentResult.error.message || 'Payment failed');

      if (paymentResult.paymentIntent?.status === 'succeeded') {
        try {
          await submitOrder(paymentIntentId);
          clearCart();
          setSuccess(true);
        } catch (err: any) {
          setError(err.message || 'Order submission failed after payment.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>Checkout</Typography>
      {success ? (
        <Box textAlign="center" mt={4}>
          <Alert severity="success">
            <Typography variant="h6" mb={1}>Payment Successful!</Typography>
            <Typography>Thank you for your order.</Typography>
          </Alert>
        </Box>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Customer Information</Typography>
                  <TextField label="Full Name" name="name" value={customerInfo.name} onChange={handleInputChange} fullWidth margin="normal" />
                  <TextField label="Email" name="email" value={customerInfo.email} onChange={handleInputChange} fullWidth margin="normal" />
                  {isLoaded ? (
                    <Autocomplete onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)} onPlaceChanged={handlePlaceChanged}>
                      <TextField label="Address" name="addressLine1" value={customerInfo.addressLine1} onChange={handleInputChange} fullWidth margin="normal" />
                    </Autocomplete>
                  ) : (
                    <TextField label="Address" name="addressLine1" value={customerInfo.addressLine1} onChange={handleInputChange} fullWidth margin="normal" />
                  )}
                  <TextField label="Address Line 2" name="addressLine2" value={customerInfo.addressLine2} onChange={handleInputChange} fullWidth margin="normal" />
                  <TextField label="City" name="city" value={customerInfo.city} fullWidth disabled margin="normal" />
                  <TextField label="State" name="state" value={customerInfo.state} fullWidth disabled margin="normal" />
                  <TextField label="Zip Code" name="zip" value={customerInfo.zip} fullWidth disabled margin="normal" />
                </CardContent>
              </Card>

              {(shippingConfig?.shipping_method === 'carrier'  || subtotal >= Number(shippingConfig?.free_shipping_threshold)) && 
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Shipping Options</Typography>
                  {subtotal >= Number(shippingConfig?.free_shipping_threshold) ? (
                    <Typography>No shipping cost — you qualify for free shipping.</Typography>
                  ) : shippingConfig?.shipping_method === 'flat_rate' ? (
                    <Typography>Flat rate shipping: ${shippingCost.toFixed(2)}</Typography>
                  ) : fetchingRates ? (
                    <Typography>Loading shipping options...</Typography>
                  ) : (
                    shippingOptions.length > 0 ? (
                      <Box>
                        {shippingOptions.map((rate) => (
                          <Box key={rate.id} mb={1}>
                            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <input
                                type="radio"
                                name="shippingRate"
                                value={rate.id}
                                checked={selectedRate === rate.id}
                                onChange={() => setSelectedRate(rate.id)}
                                style={{ marginRight: 10 }}
                              />
                              <span>{rate.carrier} {rate.service} • ${parseFloat(rate.rate).toFixed(2)} {rate.delivery_days && `• ${rate.delivery_days} day${rate.delivery_days > 1 ? 's' : ''}`}</span>
                            </label>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography>No shipping rates available.</Typography>
                    )
                  )}
                </CardContent>
              </Card>
              }

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Payment</Typography>
                  <Box sx={{ border: '1px solid #ccc', borderRadius: 1, padding: 2, mb: 2 }}>
                    <CardElement options={{
                      style: {
                        base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } },
                        invalid: { color: '#9e2146' },
                      }
                    }} />
                  </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={
                      !stripe || loading || (shippingConfig?.shipping_method === 'carrier' && !selectedRate)
                    }
                  >
                    {loading ? 'Processing...' : `Pay $${(subtotal + shippingCost).toFixed(2)}`}
                  </Button>
                  {error && <Typography color="error" variant="body2" mt={2}>{error}</Typography>}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Order Summary</Typography>
                  {cartItems.map((item, idx) => (
                    <Box key={idx} mb={2} display="flex" alignItems="center">
                      <Box component="img" src={item.primaryImage || ''} alt={item.name} sx={{ width: 64, height: 64, borderRadius: 1, objectFit: 'cover', mr: 2, border: '1px solid #ccc' }} />
                      <Box flexGrow={1}>
                        <Typography variant="body1">{item.quantity} x {item.name}</Typography>
                        {item.customDetails && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Customization: {item.customDetails}</Typography>}
                      </Box>
                      <Typography variant="body1">${(item.price * item.quantity).toFixed(2)}</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 2 }} />
                  {shippingCost > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography>Shipping</Typography>
                      <Typography>${shippingCost.toFixed(2)}</Typography>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6">${(subtotal + shippingCost).toFixed(2)}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      )}
      <Backdrop open={submittingOrder} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default Checkout;
